import React, { useEffect, useRef, useState } from 'react';
import { COPY } from './copy';
import ModalShell from './ModalShell';
import ConfirmModal from './ConfirmModal';

const TITLE_ID = 'rai-request-prompt-modal-title';

const RequestPromptModal = ({ open, onClose, onSubmit, description }) => {
  const [body, setBody] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (open) textareaRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => { if (e.key === 'Escape' && !submitting && !confirmOpen) onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, submitting, confirmOpen, onClose]);

  if (!open) return null;

  const handleClose = () => {
    setBody('');
    setError(null);
    setConfirmOpen(false);
    onClose();
  };

  const handleSendClick = () => {
    if (!body.trim()) {
      setError('Please describe the information you need.');
      return;
    }
    setError(null);
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onSubmit({ body: body.trim() });
      handleClose();
    } catch (err) {
      const msg = err.response?.data?.errors?.join(', ') || 'Failed to send request';
      setError(msg);
      setConfirmOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <ModalShell onClose={handleClose} labelledBy={TITLE_ID} zIndex={999} maxWidth={900}>
        <h2 id={TITLE_ID} className="text-lg font-semibold mb-4">{COPY.modal.title}</h2>
        <p className="mb-4 text-sm text-slate-600">{description || COPY.modal.description}</p>
        <textarea
          ref={textareaRef}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          style={{ width: '100%', minHeight: 160, padding: 8, border: '1px solid #ccc', borderRadius: 4 }}
          aria-label={COPY.modal.title}
        />
        {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
        <div className="flex flex-row gap-x-6 justify-end mt-4">
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="btn btn-ghost"
          >
            {COPY.buttons.cancel}
          </button>
          <button
            type="button"
            onClick={handleSendClick}
            disabled={submitting}
            className="btn btn-primary"
          >
            {COPY.buttons.send}
          </button>
        </div>
      </ModalShell>
      <ConfirmModal
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
        submitting={submitting}
        title={COPY.modal.confirmTitle}
      />
    </>
  );
};

export default RequestPromptModal;
